import Constants from "expo-constants";
import { Platform } from "react-native";

export type DeploymentEnvironment = "local" | "preview" | "production";

function resolveDeploymentEnvironment(): DeploymentEnvironment {
  if (Platform.OS === "web") {
    // The web build runs directly on its real hosting domains -- read the
    // environment off the actual browser location instead of threading a
    // build-time env var through Metro. (Constants.expoConfig isn't
    // reliably populated at runtime in a static web export, and Metro's
    // transform cache doesn't reliably bust on env var changes either --
    // both bit us before landing on this.) `window` is undefined during
    // the static-export prerender step (Node.js, not a browser); "local"
    // there is harmless since hydration re-evaluates this in the real
    // browser immediately after.
    if (typeof window === "undefined") return "local";
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return "local";
    // Cloudflare Workers: use.<domain> is production, dev-use.<domain> (and
    // any *.workers.dev URL) is preview.
    if (host.startsWith("dev-use.") || host.endsWith(".workers.dev"))
      return "preview";
    return "production";
  }
  // Native builds: `ENVIRONMENT` in .env (or the eas.json build profile) reaches
  // the app through app.config.ts. DEVELOPMENT is the local DDEV stack, TESTING
  // the Coolify preview API; anything else, unset included, is production.
  const requested = String(
    Constants.expoConfig?.extra?.environment ?? "",
  ).toUpperCase();
  return requested === "DEVELOPMENT"
    ? "local"
    : requested === "TESTING"
      ? "preview"
      : "production";
}

const deploymentEnvironment = resolveDeploymentEnvironment();

type EnvironmentConstants = {
  siteUrl: string;
  appUrl: string;
  apiUrl: string;
  sentryAppDsn?: string;
  turnstileSiteKey?: string;
};

// Sentry DSN is a public ingest endpoint (safe to commit). Kept undefined for
// local so dev errors don't reach the shared project; wired for preview and
// production below.
const sentryAppDsn =
  "https://b6329904808d8200e26ec3699bfe8bff@o4504087623761920.ingest.us.sentry.io/4511953350033408";
const turnstileSiteKey = "0x4AAAAAAEsjE2VptfuInggd";
const constantsByEnvironment: Record<
  DeploymentEnvironment,
  EnvironmentConstants
> = {
  local: {
    siteUrl: "http://localhost:3000",
    appUrl: "http://localhost:8081",
    // The DDEV site of florafolio-api (`ddev start` there).
    apiUrl: "https://florafolio-api.ddev.site",
    sentryAppDsn: undefined,
    // No spam check locally: the widget is not authorised for localhost.
  },
  preview: {
    siteUrl: "https://florafolio-website.pages.dev",
    appUrl: "https://dev-use.florafolio.app",
    apiUrl: "https://dev-api.florafolio.app",
    sentryAppDsn,
    turnstileSiteKey,
  },
  production: {
    siteUrl: "https://www.florafolio.app",
    appUrl: "https://use.florafolio.app",
    apiUrl: "https://api.florafolio.app",
    sentryAppDsn,
    turnstileSiteKey,
  },
};

const environmentConstants = constantsByEnvironment[deploymentEnvironment];

export const DEPLOYMENT_ENVIRONMENT = deploymentEnvironment;
export const SITE_URL = environmentConstants.siteUrl;
export const APP_URL = environmentConstants.appUrl;
export const SENTRY_APP_DSN = environmentConstants.sentryAppDsn;
export const TURNSTILE_SITE_KEY = environmentConstants.turnstileSiteKey;
// The API origin is public, so it is baked in. Override with EXPO_PUBLIC_API_URL,
// e.g. for a phone on the LAN or the e2e suite.
export const API_URL =
  process.env.EXPO_PUBLIC_API_URL?.trim() || environmentConstants.apiUrl;
export const backendConfigured = Boolean(API_URL);

export const SENTRY_ENABLED = Boolean(SENTRY_APP_DSN);
export const TURNSTILE_ENABLED = Boolean(TURNSTILE_SITE_KEY);
export const REGISTRATION_ENABLED = true;

// RevenueCat SDK keys are public identifiers, never server secrets.
// Production keys must be added here before releasing paid subscriptions.
// RevenueCat Web Billing (Stripe): live keys on production, sandbox keys (Stripe
// test cards) everywhere else.
const REVENUECAT_WEB_KEYS = {
  sandbox: "rcb_sb_AJIUijlrSTtsJVfLIuYzEhaZl",
  production: "rcb_zRslpycgUcekIUuyNpdhIfXluHlQ",
} as const;

export const REVENUECAT_API_KEYS = {
  ios: "appl_DHfKRZvRGoFOafVrQGKCcmvMGPi",
  android: "goog_xzavQygvEAnOBtJAyCPtgGUZBPk",
  // Test Store key: non-production native builds.
  test: "test_ENzvmMtrWBeqbxFkfhKqejthJCv",
  web:
    DEPLOYMENT_ENVIRONMENT === "production"
      ? REVENUECAT_WEB_KEYS.production
      : REVENUECAT_WEB_KEYS.sandbox,
} as const;

export const DEFAULT_REGIONAL_SETTINGS = {
  units: "metric",
  dateFormat: "en-NZ",
  timezone: "Pacific/Auckland",
} as const;

export const STORE_LINKS = {
  ios: "",
  android: "",
} as const;

export const EXTERNAL_LINKS = {
  privacyPolicy: `${SITE_URL}/legal/privacy-policy/`,
  terms: `${SITE_URL}/legal/terms-and-conditions/`,
  facebook: "",
  instagram: "",
  help: `${SITE_URL}/help/`,
} as const;
